import { createContext, useCallback, useContext, useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, http } from '@/services/api';
import type { Me, Role } from '@/types/user';

/**
 * Sessão do usuário (equivale ao contexts/Authentication da Impacto).
 * A fonte da verdade é GET /auth/me; os tokens ficam em cookies httpOnly (nunca no JS).
 */
interface AuthValue {
  me: Me | null;
  loading: boolean;
  /** Atualiza o usuário em memória (após login, 2FA, conclusão de cadastro). */
  setMe: (me: Me | null) => void;
  reload: () => Promise<unknown>;
  logout: () => Promise<void>;
  hasRole: (...roles: Role[]) => boolean;
}

const AuthContext = createContext<AuthValue | null>(null);
export const ME_KEY = ['auth', 'me'];

export function AuthenticationProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return await http.get<Me>('/auth/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
  });

  const setMe = useCallback((me: Me | null) => qc.setQueryData(ME_KEY, me), [qc]);

  const logout = useCallback(async () => {
    await http.post('/auth/logout').catch(() => {});
    qc.clear();
    qc.setQueryData(ME_KEY, null);
  }, [qc]);

  useEffect(() => {
    const onExpired = () => qc.setQueryData(ME_KEY, null);
    window.addEventListener('azanas:session-expired', onExpired);
    return () => window.removeEventListener('azanas:session-expired', onExpired);
  }, [qc]);

  const me = query.data ?? null;
  const value: AuthValue = {
    me,
    loading: query.isLoading,
    setMe,
    reload: () => query.refetch(),
    logout,
    hasRole: (...roles) => !!me && roles.some((r) => me.roles.includes(r)),
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth fora do AuthenticationProvider');
  return ctx;
}
